import { peaksFromAudioBuffer } from "@shared/lib/audioWaveform";

import { useChunkedTransfer } from "./useChunkedTransfer";

import type { FileAttachment } from "shared";

// Медиа-метаданные вложения: габариты (px, не путать с size — байты) + превью
// (data-URL jpeg). Генерирует отправитель — получатель показывает превью сразу,
// не скачивая файл.
interface MediaMeta {
    width?: number;
    height?: number;
    poster?: string;
    // Длительность (сек) — видео/аудио.
    duration?: number;
    // Волна — только аудио.
    waveform?: number[];
}

// Максимальная сторона превью: лента рендерит до 320 CSS px, 480 хватает и на
// умеренную retina; больше — раздувает зашифрованный контент сообщения.
const POSTER_MAX = 480;

function drawPoster(
    source: CanvasImageSource,
    width: number,
    height: number
): string | undefined {
    const scale = Math.min(1, POSTER_MAX / Math.max(width, height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx || !canvas.width) {
        return undefined;
    }
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.75);
}

// Габариты + миниатюра картинки одним декодом.
async function imageMeta(file: File): Promise<MediaMeta | null> {
    try {
        const bitmap = await createImageBitmap(file);
        const meta: MediaMeta = {
            width: bitmap.width,
            height: bitmap.height,
            poster: drawPoster(bitmap, bitmap.width, bitmap.height),
        };
        bitmap.close();
        return meta;
    } catch {
        return null;
    }
}

// Габариты + кадр видео одним offscreen-декодом (сдвиг от нуля — первый кадр
// часто чёрный).
function videoMeta(file: File): Promise<MediaMeta | null> {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement("video");
        video.preload = "auto";
        video.muted = true;
        video.playsInline = true;

        const finish = (meta: MediaMeta | null) => {
            URL.revokeObjectURL(url);
            video.src = "";
            resolve(meta);
        };
        const timer = setTimeout(() => finish(null), 5000);

        video.addEventListener("error", () => {
            clearTimeout(timer);
            finish(null);
        });
        video.addEventListener("loadeddata", () => {
            video.currentTime = Math.min(0.1, video.duration || 0);
        });
        video.addEventListener("seeked", () => {
            clearTimeout(timer);
            if (!video.videoWidth) {
                finish(null);
                return;
            }
            finish({
                width: video.videoWidth,
                height: video.videoHeight,
                poster: drawPoster(video, video.videoWidth, video.videoHeight),
                duration: Number.isFinite(video.duration)
                    ? Math.round(video.duration)
                    : undefined,
            });
        });
        video.src = url;
    });
}

// Длительность + волна аудио одним декодом (для дорожки плеера у получателя).
async function audioMeta(file: File): Promise<MediaMeta | null> {
    try {
        const buf = await file.arrayBuffer();
        const ctx = new AudioContext();
        try {
            const audio = await ctx.decodeAudioData(buf);
            return {
                duration: Math.round(audio.duration),
                waveform: peaksFromAudioBuffer(audio),
            };
        } finally {
            void ctx.close().catch(() => {});
        }
    } catch {
        return null;
    }
}

export function useFileUpload() {
    const { upload, deleteFile } = useChunkedTransfer();

    // Заливка через чанковый транспорт (память ограничена размером чанка + прогресс).
    // signal отменяет загрузку (сервер подчищается внутри транспорта).
    async function uploadFile(
        file: File,
        chatId: string,
        onProgress?: (fraction: number) => void,
        signal?: AbortSignal
    ): Promise<FileAttachment> {
        const mimeType = file.type || "application/octet-stream";
        const attachment = await upload(
            file,
            chatId,
            { name: file.name, mimeType },
            onProgress,
            signal
        );
        const meta = mimeType.startsWith("image/")
            ? await imageMeta(file)
            : mimeType.startsWith("video/")
              ? await videoMeta(file)
              : mimeType.startsWith("audio/")
                ? await audioMeta(file)
                : null;
        if (meta) {
            attachment.width = meta.width;
            attachment.height = meta.height;
            attachment.poster = meta.poster;
            attachment.duration = meta.duration;
            attachment.waveform = meta.waveform;
        }
        return attachment;
    }

    return { uploadFile, deleteFile };
}
